import React, { useEffect, useState } from 'react';

import produce from 'immer';
import { DropResult } from 'react-beautiful-dnd';
import { useQueryClient } from 'react-query';
import { WidgetView, WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { ITableColumnState } from 'hooks/useTableCurrentState';

import { useMutationSetComponentColumns } from 'api/mutations/setComponentColumns.mutation';
import { useMutationSetTableColumns } from 'api/mutations/setTableColumns.mutation';
import { useMutationSetWidgetColumns } from 'api/mutations/setWidgetColumns.mutation';
import { useQueryGetTableColumns } from 'api/queries/getTableColumns.query';

import classes from 'components/common/Modals/CustomTableColumns/customTableColumnsModal.module.scss';
import {
    MappedColumn,
    filterColumnsVisibility,
    filterRowOptions,
    getLabelFromColumn,
    isAllColumnsSelected,
    unifyColumnDefinition,
} from 'components/common/Modals/CustomTableColumns/customTableColumnsModal.utils';
import { DraggableList } from 'components/common/Modals/CustomTableColumns/draggableList';

import { reorder } from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import { IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

export interface ICustomTableColumnsModal {
    showModal: boolean;
    onHideModal: () => void;
    widgetId?: string;
    componentKey: keyof IHashmap;
    tableInstance: any;
    initialState: ITableColumnState;
    columns: any[];
    onChangeVisibility?: React.Dispatch<React.SetStateAction<{}>>;
    onChangeOrder?: React.Dispatch<React.SetStateAction<string[]>>;
    widgetViewMode?: WidgetViewMode;
}

export const CustomTableColumnsModal = ({
    showModal,
    onHideModal,
    widgetId,
    componentKey,
    tableInstance,
    initialState,
    columns,
    onChangeVisibility,
    onChangeOrder,
    widgetViewMode = WidgetView.page,
}: ICustomTableColumnsModal) => {
    const { handleSnackBar } = useCustomSnackBar();

    const [columnItems, setColumnItems] = useState<MappedColumn[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const allSelected = isAllColumnsSelected(columnItems);
    const isSearchActive = searchTerm.length > 0;
    const selectDeselectTitle = !allSelected ? 'Select all' : 'De-select all';

    // Filter columns based on search term
    const filteredColumns = searchTerm
        ? columnItems.filter((column) => getLabelFromColumn(column).toLowerCase().includes(searchTerm.toLowerCase()))
        : columnItems;

    // queries and mutations
    const queryClient = useQueryClient();
    const { mutate: mutateSetTableColumns, isLoading: isLoadingSetTableColumns } = useMutationSetTableColumns();
    const { mutate: mutateSetWidgetColumnsInPinboard, isLoading: isLoadingSetWidgetColumnsInPinboard } =
        useMutationSetWidgetColumns();
    const { mutate: mutateSetWidgetColumnsInReport, isLoading: isLoadingSetWidgetColumnsInReport } =
        useMutationSetComponentColumns();
    const { data: tableData, isLoading: tableDataLoading } = useQueryGetTableColumns({ componentKey });
    const isLoading =
        isLoadingSetTableColumns || isLoadingSetWidgetColumnsInPinboard || isLoadingSetWidgetColumnsInReport;

    // callbacks
    const onDragEnd = ({ destination, source }: DropResult) => {
        // dropped outside the list
        if (!destination) return;
        const destinationColumn = columnItems[destination.index];
        if (!destinationColumn || destinationColumn.isDisableReorder) return;
        const newItems = reorder(columnItems, source.index, destination.index);
        setColumnItems(newItems);
    };

    useEffect(() => {
        // tableData is a column configuration (order and hidden items) stored in the backend per componentKey
        // this function initializes the backend column configuration on first time widget is rendered, if it doesn't exist yet
        if (!tableData && !tableDataLoading && tableInstance) {
            const tableColumns = tableInstance.allColumns;
            const defaultColumns = tableColumns.map(unifyColumnDefinition);
            const hiddenColumns = defaultColumns.filter((item) => !item.isVisible).map((item) => item.id);
            const columnOrder = defaultColumns.map((item) => item.id);
            if (columnOrder.length > 0) {
                mutateSetTableColumns(
                    { columnOrder, hiddenColumns, componentKey },
                    {
                        onSuccess: () => {
                            tableInstance.setColumnOrder(columnOrder);
                            tableInstance.setHiddenColumns(hiddenColumns);
                            queryClient.invalidateQueries('getTableColumns');
                        },
                    },
                );
            }
        }
    }, [tableData, tableInstance]);

    useEffect(() => {
        const tableColumns = tableInstance.allColumns.filter(filterRowOptions);
        const defaultColumns = tableColumns.map(unifyColumnDefinition);
        setColumnItems(defaultColumns);
    }, [tableInstance]);

    const getColumnVisibilityAndOrder = () => {
        const table = tableInstance;
        const hiddenColumns = columnItems.filter((item) => !item.isVisible).map((item) => item.id);
        const leftColumns = table.allColumns.filter((item) => item.id === 'select-col').map((item) => item.id);
        const rightColumns = table.allColumns.filter((item) => item.id === 'rowOptions').map((item) => item.id);
        const columnOrder = [...leftColumns, ...columnItems.map((item) => item.id), ...rightColumns];

        return { hiddenColumns, columnOrder };
    };

    const updateLocalTableState = (hiddenColumns, columnOrder, queryKey) => {
        const table = tableInstance;

        table.setColumnOrder(columnOrder);
        table.setHiddenColumns(hiddenColumns);
        queryClient.invalidateQueries(queryKey);
        onChangeVisibility &&
            onChangeVisibility(
                columnItems.reduce((obj, item) => {
                    return {
                        ...obj,
                        [item.id]: !hiddenColumns.includes(item.id),
                    };
                }, {}),
            );
        onChangeOrder && onChangeOrder(columnOrder);
    };

    const onEditTableColumnsLocally = () => {
        // Edit columns locally without API call
        // Used in "add component" mode in report - when we don't have widgetId yet, and we don't want to persist changes in other places like in page.
        const { hiddenColumns, columnOrder } = getColumnVisibilityAndOrder();
        updateLocalTableState(hiddenColumns, columnOrder, 'getTableColumns');
        onHideModal();
    };

    const onEditTableColumnsInPage = () => {
        // Edit columns in page and persist changes via API call in page.
        const { hiddenColumns, columnOrder } = getColumnVisibilityAndOrder();

        mutateSetTableColumns(
            { columnOrder, hiddenColumns, componentKey },
            {
                onSuccess: () => {
                    updateLocalTableState(hiddenColumns, columnOrder, 'getTableColumns');
                    onHideModal();
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    const onEditTableColumnsInReportEditOrPinboard = () => {
        // Edit columns and persist changes via API call in report edit or in pinboard per widgetId.
        const { hiddenColumns, columnOrder } = getColumnVisibilityAndOrder();
        const mutateSetWidgetColumns = WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? mutateSetWidgetColumnsInReport
            : mutateSetWidgetColumnsInPinboard;

        mutateSetWidgetColumns(
            {
                columns: {
                    columnOrder,
                    hiddenColumns,
                },
                widgetId,
            },
            {
                onSuccess: () => {
                    updateLocalTableState(hiddenColumns, columnOrder, 'getWidgetColumns');
                    onHideModal();
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    const onHideShowItem = (e, column) => {
        const newItems = produce(columnItems, (draft) => {
            return draft.map((item: MappedColumn) => {
                if (item.id === column.id) {
                    return {
                        ...item,
                        isVisible: !item.isVisible,
                    };
                }
                return item;
            });
        });
        setColumnItems(newItems);
    };

    const onReset = ({ state }) => {
        if (!state) return null;
        setColumnItems(
            filterColumnsVisibility({
                columns,
                visibilityCondition: (item) => {
                    return state.hiddenColumns ? !state.hiddenColumns.includes(item.id) : true;
                },
            }),
        );
    };

    const onCancel = () => {
        onReset({ state: tableInstance.state });
        onHideModal();
    };

    const handleSelectAll = () => {
        setColumnItems(
            filterColumnsVisibility({
                columns,
                visibilityCondition: (item) => (!item.isDisableVisibility ? !allSelected : true),
            }),
        );
    };

    const onSaveClickHandler = () => {
        if (WidgetViewModeUtils.isReportAddWidget(widgetViewMode)) {
            onEditTableColumnsLocally();
        } else if (widgetId) {
            onEditTableColumnsInReportEditOrPinboard();
        } else {
            onEditTableColumnsInPage();
        }
    };

    return (
        <Modal
            open={showModal}
            onClose={onCancel}
            title={'Show, Hide and Reorder Columns'}
            subtitle={
                <>
                    <div>Unselect the columns you'd like to hide.</div>
                    <div>Drag columns to reorder</div>
                </>
            }
            leftActions={
                <>
                    <Button
                        variant={'secondary'}
                        onClick={() => {
                            onReset({ state: initialState });
                        }}
                    >
                        Reset
                    </Button>
                </>
            }
            actions={
                <>
                    <Button variant={'secondary'} onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant={'primary'} onClick={onSaveClickHandler} isLoading={isLoading} disabled={isLoading}>
                        Save
                    </Button>
                </>
            }
        >
            <div className={classes.modalContainer}>
                <div className={classes.searchAndSelectWrapper}>
                    <TextInput
                        placeholder={'Search column'}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        clearable={true}
                        onClear={() => setSearchTerm('')}
                    />

                    <span
                        data-testid={'select-deselect-all'}
                        className={classes.selectDeselectTitle}
                        onClick={handleSelectAll}
                    >
                        {selectDeselectTitle}
                    </span>
                </div>
                <DraggableList
                    columns={filteredColumns}
                    onDragEnd={onDragEnd}
                    onHideShowItem={onHideShowItem}
                    isDragDisabled={isSearchActive}
                />
            </div>
        </Modal>
    );
};
